/**
 * Durable delivery for compact session summaries.
 *
 * Runtime hooks only write immutable queue files. This module is the separate
 * network boundary: a CLI-triggered flush sends one capture at a time and
 * removes a file only after the server acknowledges it. The server's existing
 * `(source_client, session_id)` idempotency and monotonic merge make retried
 * snapshots safe.
 */

import {
  chmodSync,
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  renameSync,
  unlinkSync,
} from "node:fs";
import { basename, join } from "node:path";

import { buildOrgxApiUrl, type ResolvedOrgxAuth } from "./auth.js";
import {
  buildMinimalCloudSummary,
  type SessionSummary,
} from "./session-summary-backfill.js";
import {
  SESSION_SUMMARY_ENDPOINT_PATH,
  SESSION_SUMMARY_FALLBACK_ENDPOINT_PATH,
} from "./session-summary-hook.js";
import { SESSION_SUMMARY_MAX_BODY_BYTES } from "./session-summary-contract.js";

export const SESSION_SUMMARY_CAPTURE_VERSION = "orgx-session-summary-capture/v1";
export const SESSION_SUMMARY_CAPTURE_FILE_SUFFIX = ".capture.json";
export const SESSION_SUMMARY_CAPTURE_MAX_BYTES = SESSION_SUMMARY_MAX_BODY_BYTES;
export const SESSION_SUMMARY_QUARANTINE_DIR_NAME = "quarantine";
export const SESSION_SUMMARY_QUARANTINE_FILE_SUFFIX = ".malformed";

export type SessionSummaryCaptureKind =
  | "turn_boundary"
  | "run_end"
  | "session_end"
  | "legacy_unspecified";

export interface SessionSummaryCapture {
  schema_version: typeof SESSION_SUMMARY_CAPTURE_VERSION;
  capture_id: string;
  capture_kind: SessionSummaryCaptureKind;
  queued_at: string;
  session: SessionSummary;
  initiative_id?: string;
}

export interface SessionSummaryDeliveryResponse {
  ok: boolean;
  status: number;
  body?: unknown;
}

export type SessionSummaryDeliverySender = (
  url: string,
  body: Record<string, unknown>,
  headers: Record<string, string>,
) => Promise<SessionSummaryDeliveryResponse>;

export interface FlushSessionSummaryCapturesOptions {
  queueDir: string;
  auth: Pick<ResolvedOrgxAuth, "apiKey" | "baseUrl">;
  send: SessionSummaryDeliverySender;
  limit?: number;
  /** Detached workers stop after the first delivery failure while offline. */
  stopOnFailure?: boolean;
}

export interface FlushSessionSummaryCapturesResult {
  found: number;
  attempted: number;
  acknowledged: number;
  retained: number;
  malformed: number;
  quarantined: number;
  fallbackAcknowledged: number;
  receiptExpected: number;
  receiptAcknowledged: number;
  skipped?: "empty_queue" | "already_running";
  firstError?: string;
}

export function sessionSummaryCaptureFiles(queueDir: string): string[] {
  if (!existsSync(queueDir)) return [];
  try {
    return readdirSync(queueDir)
      .filter((name) => name.endsWith(SESSION_SUMMARY_CAPTURE_FILE_SUFFIX))
      .sort()
      .map((name) => join(queueDir, name));
  } catch {
    return [];
  }
}

export function sessionSummaryQuarantineDir(queueDir: string): string {
  return join(queueDir, SESSION_SUMMARY_QUARANTINE_DIR_NAME);
}

export function sessionSummaryQuarantineFiles(queueDir: string): string[] {
  const quarantineDir = sessionSummaryQuarantineDir(queueDir);
  if (!existsSync(quarantineDir)) return [];
  try {
    return readdirSync(quarantineDir)
      .filter((name) => name.includes(SESSION_SUMMARY_QUARANTINE_FILE_SUFFIX))
      .sort()
      .map((name) => join(quarantineDir, name));
  } catch {
    return [];
  }
}

function quarantineMalformedCapture(path: string, queueDir: string): string | null {
  const quarantineDir = sessionSummaryQuarantineDir(queueDir);
  try {
    mkdirSync(quarantineDir, { recursive: true, mode: 0o700 });
    const baseTarget = join(
      quarantineDir,
      `${basename(path)}${SESSION_SUMMARY_QUARANTINE_FILE_SUFFIX}`,
    );
    let target = baseTarget;
    let collision = 0;
    while (existsSync(target)) {
      collision += 1;
      target = `${baseTarget}.${collision}`;
    }
    renameSync(path, target);
    try {
      chmodSync(target, 0o600);
    } catch {
      // The capture is already durably quarantined. A permission-normalization
      // failure must not make the result claim it remained in the active queue.
    }
    return target;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function expectsAutomaticReceipt(body: Record<string, unknown>): boolean {
  const session = isRecord(body.session) ? body.session : {};
  const metadata = isRecord(session.metadata) ? session.metadata : {};
  const capture = isRecord(metadata.capture) ? metadata.capture : {};
  const execution = isRecord(metadata.execution) ? metadata.execution : {};
  const workContext = isRecord(metadata.work_context)
    ? metadata.work_context
    : {};
  const cost = isRecord(workContext.cost) ? workContext.cost : {};
  const actions = Array.isArray(execution.actions) ? execution.actions : [];
  const aggregate = isRecord(execution.completed_action_aggregate)
    ? execution.completed_action_aggregate
    : {};
  const completed = execution.actions_completed_observed;
  const omitted = execution.actions_omitted;
  const completedCount =
    typeof completed === "number" && Number.isInteger(completed) ? completed : -1;
  const omittedCount =
    typeof omitted === "number" && Number.isInteger(omitted) ? omitted : -1;
  const completedCountValid =
    completedCount >= 0;
  const omittedCountValid =
    omittedCount >= 0;
  const detailPartitionComplete =
    completedCountValid &&
    omittedCountValid &&
    actions.length === Math.min(completedCount, 32) &&
    actions.length + omittedCount === completedCount;
  const boundedAggregateComplete =
    omittedCount === 0 ||
    (aggregate.schema_version ===
      "orgx-session-completed-action-aggregate/v1" &&
      aggregate.capture_complete === true);
  const explicitWorkContext =
    workContext.schema_version === "orgx-session-work-context/v1" &&
    workContext.provenance === "producer_asserted" &&
    isRecord(workContext.intent) &&
    isRecord(workContext.authority) &&
    cost.availability === "available";

  return (
    (capture.kind === "run_end" || capture.kind === "session_end") &&
    execution.boundary === capture.kind &&
    execution.terminal_observed === true &&
    execution.action_capture_complete === true &&
    completedCountValid &&
    completedCount > 0 &&
    session.tool_calls === completedCount &&
    detailPartitionComplete &&
    boundedAggregateComplete &&
    execution.start_times_omitted === 0 &&
    execution.pending_actions === 0 &&
    actions.every((value) => {
      const action = isRecord(value) ? value : {};
      return (
        action.status !== "running" &&
        typeof action.started_at === "string" &&
        typeof action.completed_at === "string"
      );
    }) &&
    explicitWorkContext
  );
}

function confirmsAutomaticReceipt(
  response: SessionSummaryDeliveryResponse,
): boolean {
  const body = isRecord(response.body) ? response.body : {};
  const receipt = isRecord(body.automatic_receipt)
    ? body.automatic_receipt
    : {};
  return (
    receipt.ready === true &&
    receipt.persisted === true &&
    typeof receipt.receipt_id === "string" &&
    receipt.receipt_id.length > 0
  );
}

function isSessionSummary(value: unknown): value is SessionSummary {
  if (!isRecord(value)) return false;
  return (
    typeof value.schema_version === "string" &&
    typeof value.session_id === "string" &&
    typeof value.source_client === "string"
  );
}

function workGraphFingerprint(sessionId: string): string {
  let left = 0x811c9dc5;
  let right = 0x01000193;
  for (let index = 0; index < sessionId.length; index += 1) {
    left = Math.imul(left ^ sessionId.charCodeAt(index), 0x01000193) >>> 0;
    right = Math.imul(
      right ^ sessionId.charCodeAt(sessionId.length - 1 - index),
      0x85ebca6b
    ) >>> 0;
  }
  const seed = `${left.toString(16).padStart(8, "0")}${right
    .toString(16)
    .padStart(8, "0")}`;
  return `wgf_${(seed + seed).slice(0, 24)}`;
}

export function parseSessionSummaryCapture(raw: string): SessionSummaryCapture | null {
  if (Buffer.byteLength(raw, "utf8") > SESSION_SUMMARY_CAPTURE_MAX_BYTES) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    if (!isRecord(value) || value.schema_version !== SESSION_SUMMARY_CAPTURE_VERSION) return null;
    if (
      typeof value.capture_id !== "string" ||
      typeof value.queued_at !== "string" ||
      !isSessionSummary(value.session)
    ) {
      return null;
    }
    if (value.initiative_id !== undefined && typeof value.initiative_id !== "string") {
      return null;
    }
    return {
      schema_version: SESSION_SUMMARY_CAPTURE_VERSION,
      capture_id: value.capture_id,
      // Captures created by wizard 0.1.56 before this field existed must remain
      // deliverable. Preserve the uncertainty instead of guessing that an old
      // snapshot represented a real SessionEnd.
      capture_kind:
        value.capture_kind === "turn_boundary" ||
        value.capture_kind === "run_end" ||
        value.capture_kind === "session_end"
          ? value.capture_kind
          : "legacy_unspecified",
      queued_at: value.queued_at,
      session: value.session,
      ...(typeof value.initiative_id === "string"
        ? { initiative_id: value.initiative_id }
        : {}),
    };
  } catch {
    return null;
  }
}

export function readSessionSummaryCapture(path: string): SessionSummaryCapture | null {
  try {
    return parseSessionSummaryCapture(readFileSync(path, "utf8"));
  } catch {
    return null;
  }
}

function buildCaptureEndpoint(path: string, baseUrl: string): string {
  return buildOrgxApiUrl(path.replace(/^\/api/, ""), baseUrl);
}

function buildWorkGraphFallback(capture: SessionSummaryCapture): Record<string, unknown> {
  const summary = buildMinimalCloudSummary(capture.session, {
    captureId: capture.capture_id,
    captureKind: capture.capture_kind,
    queuedAt: capture.queued_at,
  });
  return {
    report: {
      schema_version: "2.0.0",
      work_graph_fingerprint: workGraphFingerprint(summary.session_id),
      session_id: summary.session_id,
      investigation: { schema_version: "2.0.0" },
      raw_transcripts_sent: false,
      events: [
        {
          source_client: summary.source_client,
          session_id: summary.session_id,
          occurred_at: summary.ended_at,
          metadata: summary,
        },
      ],
    },
  };
}

function deliveryBody(capture: SessionSummaryCapture): Record<string, unknown> {
  return {
    session: buildMinimalCloudSummary(capture.session, {
      captureId: capture.capture_id,
      captureKind: capture.capture_kind,
      queuedAt: capture.queued_at,
    }),
    ...(capture.initiative_id ? { initiative_id: capture.initiative_id } : {}),
  };
}

/**
 * Attempts each capture at most once against the primary endpoint, with a
 * single 404/405 compatibility fallback. Any other error leaves the immutable
 * capture in place for a later run. Malformed captures move to an owner-only
 * quarantine directory so they cannot block later valid delivery and remain
 * available for inspection or manual recovery.
 */
export async function flushSessionSummaryCaptures(
  options: FlushSessionSummaryCapturesOptions,
): Promise<FlushSessionSummaryCapturesResult> {
  const files = sessionSummaryCaptureFiles(options.queueDir);
  const limit = Math.max(1, Math.min(options.limit ?? 100, 1_000));
  const selected = files.slice(0, limit);
  const headers = {
    Authorization: `Bearer ${options.auth.apiKey}`,
    "Content-Type": "application/json",
  };
  const primaryUrl = buildCaptureEndpoint(
    SESSION_SUMMARY_ENDPOINT_PATH,
    options.auth.baseUrl
  );
  const fallbackUrl = buildCaptureEndpoint(
    SESSION_SUMMARY_FALLBACK_ENDPOINT_PATH,
    options.auth.baseUrl
  );
  const result: FlushSessionSummaryCapturesResult = {
    found: files.length,
    attempted: 0,
    acknowledged: 0,
    retained: 0,
    malformed: 0,
    quarantined: 0,
    fallbackAcknowledged: 0,
    receiptExpected: 0,
    receiptAcknowledged: 0,
  };

  for (const path of selected) {
    const capture = readSessionSummaryCapture(path);
    if (!capture) {
      result.malformed += 1;
      const quarantinedPath = quarantineMalformedCapture(path, options.queueDir);
      if (quarantinedPath) {
        result.quarantined += 1;
      } else {
        result.retained += 1;
        result.firstError ??= `Malformed capture could not be quarantined: ${path}`;
      }
      continue;
    }

    result.attempted += 1;
    let response: SessionSummaryDeliveryResponse;
    let usedFallback = false;
    try {
      const body = deliveryBody(capture);
      const bodyBytes = Buffer.byteLength(JSON.stringify(body), "utf8");
      if (bodyBytes > SESSION_SUMMARY_MAX_BODY_BYTES) {
        result.retained += 1;
        result.firstError ??=
          `Delivery body exceeds ${SESSION_SUMMARY_MAX_BODY_BYTES} bytes for ${capture.capture_id}`;
        if (options.stopOnFailure) break;
        continue;
      }
      const receiptExpected = expectsAutomaticReceipt(body);
      if (receiptExpected) result.receiptExpected += 1;
      response = await options.send(primaryUrl, body, headers);
      if (!response.ok && (response.status === 404 || response.status === 405)) {
        response = await options.send(fallbackUrl, buildWorkGraphFallback(capture), headers);
        usedFallback = true;
      }
      if (response.ok && receiptExpected && !confirmsAutomaticReceipt(response)) {
        result.retained += 1;
        result.firstError ??=
          `HTTP ${response.status} did not confirm automatic receipt persistence for ${capture.capture_id}`;
        if (options.stopOnFailure) break;
        continue;
      }
      if (response.ok && receiptExpected) result.receiptAcknowledged += 1;
    } catch {
      response = { ok: false, status: 0 };
    }

    if (!response.ok) {
      result.retained += 1;
      result.firstError ??= `HTTP ${response.status} for ${capture.capture_id}`;
      if (options.stopOnFailure) break;
      continue;
    }

    try {
      unlinkSync(path);
      result.acknowledged += 1;
      if (usedFallback) result.fallbackAcknowledged += 1;
    } catch {
      result.retained += 1;
      result.firstError ??= `Acknowledged capture could not be removed: ${path}`;
    }
  }

  return result;
}
